import { FC, useRef } from 'react';

import { Edit } from '@agendapro/emerald-icons';
import { useTranslation } from 'react-i18next';
import { BannerLogo, ViewMore } from '@/UI';
import { CompanyInfoProps } from './types';
import * as St from './CompanyInfo.styles';
import { ShowUp } from '@/components';
import { SliderAvatarEmployees } from '../ServicesByLocation/SliderAvatarEmployees';
import { useCompanyBookingInfo } from '@/services/company';
import { usePageContext } from '@/hooks';
import { useAdminUser } from '@/services/user';
import { EditProfileButton } from '@/UI/Atoms/EditProfileButton';
import { canEditOwnCompany, canReplyReviews, editCompanyUrls } from '@/utils/canEditOwnCompany';

export const CompanyInfo: FC<CompanyInfoProps> = ({
  logo,
  banner,
  collapsed,
  rrss,
  companyDescription = '',
  locationDescription = '',
  isLoading,
  companyName,
  mobileAddress,
  locationId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { companySlug, selectedServices, companyOverview } = usePageContext();
  // Rol/empresa desde la sesión de la app general (Cognito), que es la que autoriza
  // editar el perfil y responder reseñas — no el login del ecommerce (`auth.userData`).
  // `isError` means: there IS a back-office session cookie on this browser, but it could
  // not be renewed. Used to explain why replying is unavailable instead of hiding it.
  const { data: adminUser, isError: adminSessionExpired } = useAdminUser();
  const { data: companyBookingInfo } = useCompanyBookingInfo(companySlug);
  const canEdit = canEditOwnCompany({
    userRoleId: adminUser?.roleId,
    userCompanyId: adminUser?.companyId,
    currentCompanyId: companyOverview?.id,
  });
  const canReply = canReplyReviews({
    userRoleId: adminUser?.roleId,
    userCompanyId: adminUser?.companyId,
    currentCompanyId: companyOverview?.id,
  });

  return (
    <St.CompanyInfoContainer collapsed={collapsed} hasBanner ref={containerRef}>
      <St.HeroWrapper>
        <BannerLogo banner={banner} logo={logo} canEdit={canEdit} locationId={locationId} />
      </St.HeroWrapper>
      <ViewMore
        textContent={companyDescription}
        textContentSecondary={locationDescription}
        maxNumOfLines={2}
        arial-label="companyDescription"
        className="companyDescription"
        data-testid="companyDescription"
        isLoading={isLoading}
        rrss={rrss}
        companyName={companyName}
        showReviews
        logo={logo}
        canEdit={canEdit}
        reviewsv2={companyOverview?.reviewsMsFf}
        canReplyReviews={canReply}
        reviewsSessionExpired={adminSessionExpired}
      />
      {canEdit && (
        <EditProfileButton
          className="flex lg:hidden h-fit w-full mb-2 bg-background-primary border border-border-brand border-solid rounded-md p-1 flew-row items-center justify-center gap-1 text-text-brand text-sm hover:bg-background-secondary transition-colors duration-300"
          url={editCompanyUrls.company}
          mixpanelEvent="editCompany"
        >
          <Edit size={16} className="[&>path]:fill-icon-brand" />
          {t('EDIT_COMPANY')}
        </EditProfileButton>
      )}
      {mobileAddress && <ShowUp untilTo="XLarge">{mobileAddress}</ShowUp>}
      {companyBookingInfo?.showProviders !== 2 && (
        <ShowUp untilTo="XLarge">
          <SliderAvatarEmployees hasSelectedServices={!!selectedServices} canEdit={canEdit} />
        </ShowUp>
      )}
    </St.CompanyInfoContainer>
  );
};
